import { AuthContext, useAuthContext, useUser } from '@folklore/auth';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { useAuthCheck } from '../hooks/useAuth';
import { Bot, detectBrowserBot, headerBot, mergeBots } from '../lib/bots';

interface AuthContext {
    user: User | null;
    isLoading: boolean;
    isBot: boolean;
    bot: Bot | null;
    setUser: () => void;
}

export function useIsCheckingAuth(): boolean {
    const { isLoading } = useAuthContext() as AuthContext;
    return isLoading;
}

export function useUserRole(): string | null {
    const { role = null } = useUser() || {};
    return role as string | null;
}

export function useAttributionKey(): string | null {
    const { attributionKey = null } = useUser() || {};
    return attributionKey;
}

export function useIsAdmin(): boolean {
    const role = useUserRole();
    return ['admin'].indexOf(role ?? '') !== -1;
}

export function useIsStaff(): boolean {
    const role = useUserRole();
    return ['staff'].indexOf(role ?? '') !== -1;
}

export function useUserAuthor(): Author | null {
    const { author = null } = useUser() || {};
    return author;
}

export function useIsAuthor(): boolean {
    const author = useUserAuthor();
    return author !== null;
}

export function useIsBot(): boolean {
    const { isBot = false } = useAuthContext() as AuthContext;
    return isBot;
}

// The bot of the visitor, from the SSR header, the API (Cloudflare and user agent) and the
// browser, or null until the browser was checked
export function useBot(): Bot | null {
    const { bot = null } = useAuthContext() as AuthContext;
    return bot;
}

export function useIsCollaborator(): boolean {
    const role = useUserRole();
    return ['collaborator'].indexOf(role ?? '') !== -1;
}

export function useHasJoinedGroup(handle: string): boolean {
    const { groups = null } = useUser() || {};
    const { joined = false } =
        (groups || []).find(({ handle: groupHandle }) => groupHandle === handle) || {};
    return joined;
}

export function useIsInGroup(handle: string): boolean {
    const { groups = null } = useUser() || {};
    const group = (groups || []).find(({ handle: groupHandle }) => groupHandle === handle) || null;
    return group !== null;
}

export function useHasPermission(feature: string): boolean {
    const role = useUserRole();
    const isAdmin = useIsAdmin();
    const hasJoinedPro = useHasJoinedGroup('pro');
    switch (feature) {
        case 'attribution':
            return (
                isAdmin ||
                ['admin', 'staff', 'collaborator'].indexOf(role ?? '') !== -1 ||
                hasJoinedPro
            );
        case 'admin_menu':
            return isAdmin || ['admin', 'staff', 'collaborator'].indexOf(role ?? '') !== -1;
        case 'edit_document':
            return isAdmin || ['admin', 'staff'].indexOf(role ?? '') !== -1;
        case 'export_contest':
            return isAdmin || ['admin', 'staff'].indexOf(role ?? '') !== -1;
        case 'export_form':
            return isAdmin || ['admin', 'staff'].indexOf(role ?? '') !== -1;
        case 'manage_comments':
            return isAdmin || ['admin', 'staff'].indexOf(role ?? '') !== -1;
    }
    return false;
}

interface AuthContextProviderProps {
    disabled?: boolean;
    isBot?: boolean;
    children: ReactNode;
}

export function AuthContextProvider({
    disabled = false,
    isBot = false,
    children,
}: AuthContextProviderProps) {
    const {
        user = null,
        bot: apiBot = null,
        isLoading = false,
    } = useAuthCheck({
        enabled: !disabled && !isBot,
    });

    // Checked after the hydration, since the server can't know it
    const [browserBot, setBrowserBot] = useState<Bot | null>(null);
    useEffect(() => {
        setBrowserBot(detectBrowserBot());
    }, []);

    const bot = useMemo(
        () =>
            browserBot !== null ? mergeBots(isBot ? headerBot : null, apiBot, browserBot) : null,
        [isBot, apiBot, browserBot],
    );

    const value = {
        user: user !== null ? { ...user, attributionKey: btoa(`user_${user.id}`) } : null,
        isLoading,
        isBot: bot !== null ? bot.isBot : isBot,
        bot,
        setUser: () => {},
    };

    return <AuthContext value={value}>{children}</AuthContext>;
}
